import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { Icon } from "../icons";

export default function ProfileView({ user, isAdmin, onLogout, onSave, units = "lbs", onUnitsChange }) {
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender]           = useState("");
  const [birthday, setBirthday]       = useState("");
  const [liftingStyle, setLiftingStyle] = useState("");
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [theme, setTheme]             = useState(() => localStorage.getItem("ft_theme") || "dark");

  useEffect(() => {
    apiFetch("/profile").then(p => {
      setDisplayName(p.display_name || "");
      setGender(p.gender || "");
      setBirthday(p.birthday ? p.birthday.split("T")[0] : "");
      setLiftingStyle(p.lifting_style || "");
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const calcAge = dob => {
    if (!dob) return null;
    return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch("/profile", { method: "PUT", body: { display_name: displayName, gender, birthday, lifting_style: liftingStyle } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onSave) onSave(displayName);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const age = calcAge(birthday);

  if (loading) return (
    <div style={{ textAlign: "center", padding: "60px 0" }}>
      <div className="label-caps">LOADING...</div>
    </div>
  );

  const nameDisplay = displayName || user;

  return (
    <div className="fade-in">

      {/* ── HERO PROFILE ── */}
      <section style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 40, marginTop: 8 }}>
        <div style={{ position: "relative", marginBottom: 20 }}>
          <div style={{
            width: 96, height: 96, borderRadius: 12, overflow: "hidden",
            border: "2px solid rgba(185,200,222,0.3)", padding: 4,
          }}>
            <div style={{
              width: "100%", height: "100%", borderRadius: 8,
              background: "var(--bg-highest)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 28, fontWeight: 900, color: "#b9c8de",
            }}>
              {nameDisplay.slice(0, 2).toUpperCase()}
            </div>
          </div>
          {/* Rank badge */}
          <div style={{
            position: "absolute", bottom: -8, left: "50%", transform: "translateX(-50%)",
            background: "#b9c8de", borderRadius: 8, padding: "2px 10px",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 9, fontWeight: 700, letterSpacing: "0.15em",
            color: "#233143", whiteSpace: "nowrap",
          }}>
            {isAdmin ? "RANK: ADMIN" : "RANK: ATHLETE"}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em",
            textTransform: "uppercase", color: "var(--text)", marginBottom: 4,
          }}>{nameDisplay}</h2>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif",
            color: "#b9c8de", fontWeight: 500, letterSpacing: "0.2em",
            fontSize: 10, textTransform: "uppercase",
          }}>
            {isAdmin ? "ELITE ADMIN" : "ELITE TIER ATHLETE"}
            {age ? ` · ${age} YRS` : ""}
            {gender ? ` · ${gender.toUpperCase()}` : ""}
          </p>
          {liftingStyle && (
            <div style={{ marginTop: 10 }}>
              <span style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 11, fontWeight: 700, color: "#b9c8de",
                letterSpacing: "0.1em", textTransform: "uppercase",
                background: "rgba(185,200,222,0.12)", padding: "4px 12px", borderRadius: 6,
              }}>{liftingStyle}</span>
            </div>
          )}
        </div>
      </section>

      {/* ── IDENTITY & SPECS ── */}
      <section style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 1, background: "rgba(68,71,76,0.25)" }}/>
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: "0.3em", color: "#64748b", textTransform: "uppercase" }}>IDENTITY &amp; SPECS</span>
          <div style={{ flex: 1, height: 1, background: "rgba(68,71,76,0.25)" }}/>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Display name */}
          <div style={{
            background: "var(--bg-low)", padding: 16, borderRadius: 10,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            border: "1px solid rgba(68,71,76,0.12)",
          }}>
            <div>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#64748b", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>Display Name</div>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Your name"
                style={{
                  background: "transparent", border: "none", outline: "none",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 14, fontWeight: 500, color: "var(--text)", width: "100%",
                }}
              />
            </div>
            <div style={{ color: "#64748b", flexShrink: 0 }}><Icon.Edit/></div>
          </div>

          {/* Gender + Birthday */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ background: "var(--bg-low)", padding: 16, borderRadius: 10, border: "1px solid rgba(68,71,76,0.12)" }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#64748b", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>Gender</div>
              <select
                value={gender}
                onChange={e => setGender(e.target.value)}
                style={{
                  background: "transparent", border: "none", outline: "none",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 13, fontWeight: 500, color: "var(--text)", width: "100%",
                  cursor: "pointer",
                }}
              >
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={{ background: "var(--bg-low)", padding: 16, borderRadius: 10, border: "1px solid rgba(68,71,76,0.12)" }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#64748b", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>Birthday</div>
              <input
                type="date" value={birthday}
                max={new Date().toISOString().split("T")[0]}
                onChange={e => setBirthday(e.target.value)}
                style={{
                  background: "transparent", border: "none", outline: "none",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 11, fontWeight: 500, color: "var(--text)", width: "100%",
                }}
              />
            </div>
          </div>

          {/* Lifting style */}
          <div style={{
            background: "var(--bg-highest)", padding: 18, borderRadius: 10,
            border: "1px solid rgba(185,200,222,0.12)",
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: 8, right: 8, opacity: 0.08 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="#b9c8de"><path d="M6 4v16M18 4v16M3 8h18M3 16h18"/></svg>
            </div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 9, color: "#94a3b8", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 12, fontWeight: 700 }}>LIFTING STYLE</div>
            {liftingStyle && (
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, color: "#b9c8de", fontWeight: 700, textTransform: "uppercase", fontStyle: "italic", letterSpacing: "-0.02em", marginBottom: 10 }}>
                {liftingStyle}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {["Bodybuilding", "Weightlifting", "Powerlifting", "General Fitness"].map(s => (
                <button key={s}
                  onClick={() => setLiftingStyle(liftingStyle === s ? "" : s)}
                  style={{
                    padding: "10px 8px", borderRadius: 6,
                    background: liftingStyle === s ? "rgba(185,200,222,0.15)" : "var(--bg-low)",
                    border: `1px solid ${liftingStyle === s ? "rgba(185,200,222,0.3)" : "rgba(68,71,76,0.3)"}`,
                    color: liftingStyle === s ? "#b9c8de" : "#8e9197",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                    textTransform: "uppercase", cursor: "pointer",
                  }}>{s}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── INTERFACE SECTION ── */}
      <section style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 1, background: "rgba(68,71,76,0.25)" }}/>
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: "0.3em", color: "#64748b", textTransform: "uppercase" }}>INTERFACE</span>
          <div style={{ flex: 1, height: 1, background: "rgba(68,71,76,0.25)" }}/>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          {/* Theme */}
          <div style={{ background: "var(--bg-low)", padding: 16, borderRadius: 10, border: "1px solid rgba(68,71,76,0.12)" }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#64748b", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>Theme</div>
            <div style={{ display: "flex", gap: 6 }}>
              {["dark", "light"].map(t => (
                <button key={t} onClick={() => { setTheme(t); localStorage.setItem("ft_theme", t); document.documentElement.setAttribute("data-theme", t); }}
                  style={{
                    flex: 1, padding: "7px 0", borderRadius: 6, cursor: "pointer",
                    fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, fontWeight: 700,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    background: theme === t ? "rgba(185,200,222,0.15)" : "var(--bg-highest)",
                    border: `1px solid ${theme === t ? "rgba(185,200,222,0.3)" : "rgba(68,71,76,0.3)"}`,
                    color: theme === t ? "#b9c8de" : "#64748b",
                  }}>{t}</button>
              ))}
            </div>
          </div>
          {/* Units */}
          <div style={{ background: "var(--bg-low)", padding: 16, borderRadius: 10, border: "1px solid rgba(68,71,76,0.12)" }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#64748b", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>Units</div>
            <div style={{ display: "flex", gap: 6 }}>
              {["lbs", "kg"].map(u => (
                <button key={u} onClick={() => onUnitsChange && onUnitsChange(u)}
                  style={{
                    flex: 1, padding: "7px 0", borderRadius: 6, cursor: "pointer",
                    fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, fontWeight: 700,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    background: units === u ? "rgba(185,200,222,0.15)" : "var(--bg-highest)",
                    border: `1px solid ${units === u ? "rgba(185,200,222,0.3)" : "rgba(68,71,76,0.3)"}`,
                    color: units === u ? "#b9c8de" : "#64748b",
                  }}>{u}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: "100%", padding: 14, borderRadius: 10, border: "none",
            background: saved
              ? "rgba(185,200,222,0.15)"
              : "linear-gradient(135deg, #b9c8de 0%, #94a3b8 100%)",
            color: saved ? "#b9c8de" : "#233143",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 900, fontSize: 12, letterSpacing: "0.15em",
            textTransform: "uppercase", cursor: "pointer",
            transition: "all 0.15s",
            border: saved ? "1px solid rgba(185,200,222,0.3)" : "none",
          }}
        >
          {saving ? "SAVING…" : saved ? "✓ SAVED" : "SAVE CHANGES"}
        </button>
      </section>

      {/* ── SIGN OUT ── */}
      <section style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 1, background: "rgba(68,71,76,0.25)" }}/>
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: "0.3em", color: "#64748b", textTransform: "uppercase" }}>SESSION</span>
          <div style={{ flex: 1, height: 1, background: "rgba(68,71,76,0.25)" }}/>
        </div>
        <button onClick={onLogout} style={{
          width: "100%", padding: 16, borderRadius: 10,
          background: "var(--bg-low)", border: "1px solid rgba(68,71,76,0.2)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          cursor: "pointer", transition: "all 0.15s",
        }}>
          <Icon.LogOut/>
          <span style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.2em",
            textTransform: "uppercase", color: "#64748b",
          }}>SIGN OUT</span>
        </button>
      </section>

      <div style={{
        textAlign: "center", marginTop: 20,
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: 9, color: "#44474c", letterSpacing: "0.15em", textTransform: "uppercase",
      }}>VERSION 2.4.1 // BUILD INSTINCT</div>
    </div>
  );
}
